import {
  type CylinderSurface,
  cylinderPlacementIssue,
  GEOMETRY_TOLERANCE,
  type PlacementPosition,
  type SizeMm,
  type SurfacePoint,
} from '@repo/api-contracts';
import {
  cylinderCenterScale,
  cylinderMmToPhoto,
  cylinderPhotoToMm,
  frameToPhoto,
} from '@repo/design-renderer';
import { type ArcSegment, type CylinderParams, visibleArcSegments } from '@repo/warp-engine';
import type { ComponentGrid } from './component-grid';
import { cylinderParamsOf } from './cylinder-surface';
import { clamp, type PhotoDimensions, type PhotoPolyline } from './flat-surface-mapping';

export { cylinderCenterScale, cylinderMmToPhoto, cylinderPhotoToMm };

/** 클램프가 남기는 가장 작은 가로 겹침(mm). 계약은 겹침이 허용오차보다 커야 받으므로 그 두 배를 남긴다. */
const MIN_CLAMPED_OVERLAP_MM = 2 * GEOMETRY_TOLERANCE;

/**
 * 끌어 옮긴 원통 배치 위치를 계약 원통 배치 규칙(cylinderPlacementIssue, ADR-0007 D9) 안으로 자른다.
 * - 가로: 프레임 [0, W] 와의 겹침이 허용오차보다 크게 남는 [겹침 − w, W − 겹침] 으로 자른다. 음수 위치도 받는다.
 * - 세로: 프레임 안에 모두 들어가는 [0, H − h] 로 자른다.
 * 감김(둘레 2W)은 보지 않는다 — 계약은 감지 않은 [x, x + w] 와 [0, W] 의 겹침으로 판정하고, 감김은 그리기만 따른다.
 * Component 가 둘레 2W 보다 넓거나 프레임보다 높아 어디에도 둘 수 없으면 null 이다.
 * 결과는 같은 계약 함수로 한 번 더 확인해, 계약이 받지 않는 위치를 돌려주지 않는다.
 */
export const clampCylinderPlacementPosition = (
  component: SizeMm,
  frame: SizeMm,
  position: PlacementPosition,
): PlacementPosition | null => {
  const clamped = {
    xMm: clamp(
      position.xMm,
      MIN_CLAMPED_OVERLAP_MM - component.widthMm,
      frame.widthMm - MIN_CLAMPED_OVERLAP_MM,
    ),
    yMm: clamp(position.yMm, 0, Math.max(0, frame.heightMm - component.heightMm)),
  };

  return cylinderPlacementIssue(component, frame, clamped) === null ? clamped : null;
};

/** 배치 안 mm 격자선을 사진 위 선으로 옮긴 결과. 세로선은 두 점의 곧은 선, 가로선은 표본한 곡선이다. */
export interface PhotoGridLines {
  readonly vertical: readonly PhotoPolyline[];
  readonly horizontal: readonly PhotoPolyline[];
}

/** 앞면 창 전체(폭 W)를 이 수만큼 같은 각도로 나눠 곡선을 표본한다. 조각은 폭에 비례해 나눈다. */
const ARC_SAMPLES_PER_FRAME = 64;

/** 가로 조각 [fromMm, toMm] 을 같은 간격으로 나눈 프레임 x 들. 프레임 x 는 원통 각도에 비례한다. 끝은 정확히 toMm 이다. */
const sampleArc = ({ fromMm, toMm }: ArcSegment, frameWidthMm: number): number[] => {
  const steps = Math.max(1, Math.ceil((ARC_SAMPLES_PER_FRAME * (toMm - fromMm)) / frameWidthMm));

  return Array.from({ length: steps + 1 }, (_, index) =>
    index === steps ? toMm : fromMm + ((toMm - fromMm) * index) / steps,
  );
};

/** 세로 구간 [yMm, yMm + heightMm] 을 프레임 높이 [0, H] 로 자른다. 그 밖은 원통 변환이 없다. 남는 높이가 없으면 null. */
const verticalSpanInFrame = (
  yMm: number,
  heightMm: number,
  frameHeightMm: number,
): { readonly top: number; readonly bottom: number } | null => {
  const top = Math.max(0, yMm);
  const bottom = Math.min(frameHeightMm, yMm + heightMm);

  return bottom > top ? { top, bottom } : null;
};

/** 같은 프레임 y 의 프레임 x 들을 사진 점으로 옮긴다. 옮길 수 없는 점이 하나라도 있으면(엔진 검사 실패) null. */
const projectRow = (
  params: CylinderParams,
  frame: SizeMm,
  xs: readonly number[],
  yMm: number,
): PhotoPolyline | null => {
  const points = xs.map((xMm) => frameToPhoto(params, frame, { xMm, yMm }));

  return points.every((point): point is SurfacePoint => point !== null) ? points : null;
};

/**
 * 프레임 위 사각형(mm)을 사진 위 닫힌 곡선으로 옮긴다 — componentRectOnPhoto 의 원통판이다.
 * size·position 에 Component 와 배치 위치를 주면 배치 경계, 프레임(realSize)과 (0, 0) 을 주면 표면 실루엣이다.
 * - 가로: 둘레 2W 로 감아 앞면 창 [0, W] 에 보이는 조각(visibleArcSegments, 결정 Q6)마다 선 하나를 만든다. 최대 두 조각이다.
 * - 세로: 프레임 높이 [0, H] 로 자른다. 그 밖은 원통 변환이 없어 그리지 않는다.
 * 선은 위 변을 왼쪽→오른쪽, 아래 변을 오른쪽→왼쪽으로 표본하고 마지막 점에서 첫 점으로 닫는다.
 * 왼쪽·오른쪽 변은 같은 프레임 x 가 같은 사진 x 로 가는 곧은 선이라 따로 표본하지 않는다.
 * 보이는 부분이 없으면 빈 배열이다.
 */
export const cylinderRectOnPhoto = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
  size: SizeMm,
  position: PlacementPosition,
): readonly PhotoPolyline[] => {
  const frame = surface.realSize;
  const span = verticalSpanInFrame(position.yMm, size.heightMm, frame.heightMm);
  if (span === null) return [];
  const params = cylinderParamsOf(surface, photo);

  return visibleArcSegments(position.xMm, size.widthMm, frame.widthMm).flatMap((segment) => {
    const xs = sampleArc(segment, frame.widthMm);
    const top = projectRow(params, frame, xs, span.top);
    const bottom = projectRow(params, frame, [...xs].reverse(), span.bottom);

    return top === null || bottom === null ? [] : [[...top, ...bottom]];
  });
};

/**
 * 배치 안 mm 격자선(componentGrid 의 위치, 배치 왼쪽 위 기준 mm)을 사진 위 선으로 옮긴다.
 * - 세로선: 같은 프레임 x 는 사진에서도 같은 x 라 위·아래 두 점의 곧은 선이다. 둘레로 감아 뒷면에 놓이는 선은 뺀다.
 * - 가로선: 배치 가로 구간이 앞면에 보이는 조각마다 표본한 곡선이다(cylinderRectOnPhoto 와 같은 감김).
 * 세로는 프레임 높이 [0, H] 로 자르고, 그 밖의 가로선은 뺀다.
 */
export const cylinderGridOnPhoto = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
  size: SizeMm,
  position: PlacementPosition,
  grid: Pick<ComponentGrid, 'vertical' | 'horizontal'>,
): PhotoGridLines => {
  const frame = surface.realSize;
  const span = verticalSpanInFrame(position.yMm, size.heightMm, frame.heightMm);
  if (span === null) return { vertical: [], horizontal: [] };
  const params = cylinderParamsOf(surface, photo);
  const rows = visibleArcSegments(position.xMm, size.widthMm, frame.widthMm).map((segment) =>
    sampleArc(segment, frame.widthMm),
  );

  return {
    vertical: grid.vertical.flatMap((offsetMm) => {
      const xMm = position.xMm + offsetMm;
      const top = frameToPhoto(params, frame, { xMm, yMm: span.top });
      const bottom = frameToPhoto(params, frame, { xMm, yMm: span.bottom });
      return top === null || bottom === null ? [] : [[top, bottom]];
    }),
    horizontal: grid.horizontal.flatMap((offsetMm) =>
      rows.flatMap((xs) => {
        const line = projectRow(params, frame, xs, position.yMm + offsetMm);
        return line === null ? [] : [line];
      }),
    ),
  };
};
